import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { DocumentService } from 'src/app/core/service/document.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FulfillmentMeta } from 'src/app/assessment/model/assessment';
import { TokenCaptureLink } from '../model/token-capture-link';
import { domToPng } from 'modern-screenshot'
import { TokenChangeStatus } from '../model/token-change-status';
import { Base64Util } from 'src/app/core/utility/base64-util';
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { applyFormlyPatientContext } from 'src/app/core/utility/formly-patient-context.util';
import { ConsentCaptureResponse } from '../model/consent-capture-response';

@Component({
  standalone: false,
  selector: 'app-document-link-consent',
  templateUrl: './document-link-consent.component.html',
  //styleUrls: ['./document-link-consent.component.scss']
  encapsulation: ViewEncapsulation.None,
  styles: [`
  .completed-container {
    margin: auto;
    font-size: 14pt;
    border: solid 1px;
    padding: 10px;
    background-color: #78924f;
    color: #fff;
    border-radius: 8px;
    width: 250px;
  }`
  ]
})
export class DocumentLinkConsentComponent implements OnInit
{
  @ViewChild('imageSection') public imageSection!: any;

  public model: TokenCaptureLink = new TokenCaptureLink();
  public member: any = null;
  public memberName: string;
  public assessmentName: string;
  public isValidToken = false;
  public validating = true;
  public uploading = false;
  public completed = false;
  public hasReportImage = false;
  public reportImageLoaded = false;
  public reportImageUrl: string;
  public formFulfillment = new UntypedFormGroup({});
  public consentModel: any = {};
  public fields: FormlyFieldConfig[] = [];
  public formlyOptions: FormlyFormOptions = { formState: { patient: null } };
  public hiddingSignatureButtons = false;
  public signatureImage = null;
  public signedDate = new Date();

  constructor(
    private route: ActivatedRoute,
    private messageHandlerService: MessageHandlerService,
    private documentService: DocumentService,
    private assessmentService: AssessmentService)
  {
  }

  public get bypassSignature(): boolean
  {
    return this.consentModel && this.consentModel.firstSign === 'No';
  }

  public ngOnInit(): void
  {
    this.getReportPhotoFromService();
    this.initializations();
    this.route.params.forEach((params: Params) =>
    {
      if (params.token !== undefined)
      {
        this.model.token = params.token;
        delete this.model.snapshot;
        delete this.model.mimeType;
        this.documentService.verifyConsentCapture(this.model).subscribe({
          next: (data: ConsentCaptureResponse) =>
          {
            if (data !== null && data !== undefined)
            {
              this.memberName = data.memberName;
              this.assessmentName = data.assessmentName;
              this.fields = JSON.parse(Base64Util.decodeUnicode(data.fields));
              this.setConsentFormlyPatient(data.member);
              this.isValidToken = true;
            }
            else
            {
              this.isValidToken = false;
            }
          },
          complete: () =>
          {
            this.validating = false;
          }
        });
      }
    });
  }

  public uploadConsent(data: any): void
  {
    // Validation
    if (!this.formFulfillment.valid)
    {
      this.messageHandlerService.displayError('ASSESSMENT-FULFILLMENT-FORM-INVALID');
      return;
    }
    this.signatureImage = data.signature;
    if (this.signatureImage === null)
    {
      this.completed = true;
      this.uploading = false;

      const changeStatusData: TokenChangeStatus = new TokenChangeStatus();
      changeStatusData.token = this.model.token;
      changeStatusData.memberConsentStatus = 1; // DECLINED
      this.documentService.updateMemberConsentStatus(changeStatusData).subscribe({
        next: () =>
        { },
        error: (errorMessage) =>
        {
          this.messageHandlerService.displayError(errorMessage);
          this.uploading = false;
        },
      });
    }
  }

  public signatureImageLoaded(): void 
  {
    this.uploading = true;
    this.hiddingSignatureButtons = true;

    const excludedFilter = (node: HTMLElement) =>
    {
      const exclusionClasses = ['k-button', 'k-form-error', 'exclude-upload'];
      return !exclusionClasses.some((classname) => node.classList?.contains(classname));
    }

    // For each exclude-upload, must add the d-none to avoid blank
    const htmlContent = this.imageSection.nativeElement;
    htmlContent.querySelectorAll('.exclude-upload').forEach(
      (divElement: any) =>
      {
        divElement.classList.add('d-none');
      }
    )

    domToPng(htmlContent, { backgroundColor: '#fff', filter: excludedFilter }).then(dataUrl =>
    {
      const meta: FulfillmentMeta = new FulfillmentMeta();
      meta.data = this.consentModel;
      meta.fields = this.fields;

      const consentUpload: TokenCaptureLink = new TokenCaptureLink();
      consentUpload.token = this.model.token;
      consentUpload.mimeType = 'image/png';
      consentUpload.snapshot = dataUrl.split('base64,')[1];

      consentUpload.assessmentFulfillment = {};
      consentUpload.assessmentFulfillment.name = this.assessmentName.replace(/-/g, '_').replace(/ /g, '_') + '.json';
      consentUpload.assessmentFulfillment.mimeType = 'application/json';
      consentUpload.assessmentFulfillment.file = Base64Util.encodeUnicode(JSON.stringify(meta));

      const changeStatusData: TokenChangeStatus = new TokenChangeStatus();
      changeStatusData.token = this.model.token;
      changeStatusData.memberConsentStatus = -1;

      this.documentService.uploadConsentCapture(consentUpload).subscribe({
        complete: () =>
        {
          this.documentService.updateMemberConsentStatus(changeStatusData).subscribe({
            next: () =>
            {
              this.documentService.updateCaptureLink(this.model).subscribe({
                next: () =>
                {
                  this.completed = true;
                  this.uploading = false;
                },
                error: (errorMessage) =>
                {
                  this.messageHandlerService.displayError(errorMessage);
                  this.uploading = false;
                },
              });
            },
            error: (errorMessage) =>
            {
              this.messageHandlerService.displayError(errorMessage);
              this.uploading = false;
            },
          });
        },
        error: (errorMessage) =>
        {
          this.messageHandlerService.displayError(errorMessage);
          this.uploading = false;
        }
      });
    })
  }

  private setConsentFormlyPatient(member: any): void
  {
    if (member?.dateOfBirth != null && typeof member.dateOfBirth === 'string' && member.dateOfBirth.length === 10)
    {
      member.dateOfBirth = new Date(`${member.dateOfBirth}T12:00:00.000`);
    }

    this.member = member;
    applyFormlyPatientContext(this.assessmentService, this.fields, this.consentModel, this.formlyOptions, member);
  }

  private syncPatientFormState(member: any): void
  {
    this.formlyOptions.formState.patient = member ?? null;
  }

  private initializations(): void
  {
    this.member = null;
    this.memberName = null;
    this.validating = true;
    this.isValidToken = false;
    this.completed = false;
  }

  private getReportPhotoFromService()
  {
    this.reportImageLoaded = false;
    this.hasReportImage = false;
    this.documentService.getReportImage().subscribe({
      next: (data) =>
      {
        this.hasReportImage = data && data.content !== null;
        if (this.hasReportImage)
        {
          const base64 = data.content;
          this.reportImageUrl = 'data:image/jpeg;base64,' + base64;
        }
        this.reportImageLoaded = true;
      }
    });
  }
}
